interface Header {
  guid: string;
  display_name: string;
}

export interface liveboardAndDataObjects {
  parentName: string;
  parentID: string;
  creationDate: string;
  childObjects: { id: string; name: string }[];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isHeaderArray(arr: unknown): arr is Header[] {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  return arr.every(
    (item) =>
      isPlainObject(item) &&
      typeof (item as any).guid === "string" &&
      typeof (item as any).display_name === "string",
  );
}

/**
 * Depth‑first walk that collects every matching Header array.
 * `found` is mutated in‑place.
 */
function walk(
  node: unknown,
  found: Header[],
  visited = new Set<unknown>(),
): void {
  if (visited.has(node)) return;
  visited.add(node);

  if (Array.isArray(node)) {
    for (const el of node) walk(el, found, visited);
    return;
  }

  if (isPlainObject(node)) {
    // ── Look for the target property ───────────────────────
    if (Object.prototype.hasOwnProperty.call(node, "referenced_tables")) {
      const candidate = (node as any).referenced_tables;
      if (isHeaderArray(candidate)) {
        // push copies so later mutations don’t affect our result
        found.push(
          ...candidate.map((h) => ({
            guid: h.guid,
            display_name: h.display_name,
          })),
        );
      }
    }

    // ── Recurse into every own property ─────────────────────
    for (const key of Object.keys(node)) {
      walk((node as any)[key], found, visited);
    }
  }
}

/**
 * Extracts all unique `Header` objects from the data
 */
export async function extractUniqueHeadersLiveboards(staticData: any) {
  const resultObject: liveboardAndDataObjects[] = [];

  staticData.forEach((data: any) => {
    // Collect every header occurrence (may include duplicates)
    const allHeaders: Header[] = [];
    walk(data, allHeaders);

    // De‑duplicate using a Map keyed by `${guid}|${display_name}`
    const uniqMap = new Map<string, Header>();
    for (const hdr of allHeaders) {
      const key = `${hdr.guid}|${hdr.display_name}`;
      if (!uniqMap.has(key)) uniqMap.set(key, hdr);
    }

    const uniqueDataObjects = Array.from(uniqMap.values()).map((dataObject) => {
      return { id: dataObject.guid, name: dataObject.display_name };
    });

    let epochSeconds: number = 0;

    if (data && data.metadata_header && data.metadata_header.created) {
      epochSeconds = data.metadata_header.created;
    }
    const dateObject: Date = new Date(epochSeconds);

    resultObject.push({
      parentID: data.metadata_id,
      parentName: data.metadata_name,
      creationDate: dateObject.toISOString() + " UTC",
      childObjects: uniqueDataObjects,
    });
  });

  return resultObject;
}
